import React from 'react';
import { Bar } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate } from '../lib/format';
import { Card, Spinner, ErrorBox, DataTable, Delta, Empty } from '../components/ui';

// Side-by-side location leaderboard: sales, orders, AOV, tips, labor hours
// and sales per labor hour — the cross-location productivity view.
export default function LocationsPage() {
  const { reportParams, startDate, endDate, compareMode } = useFilters();
  const { data, loading, error } = useReport('locations-performance', { ...reportParams, compare: compareMode });

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Comparing locations…" />;
  if (data.length === 0) return <Empty />;

  const top = [...data].sort((a, b) => b.netSales - a.netSales).slice(0, 15);
  const barData = {
    labels: top.map(r => r.locationName),
    datasets: [{
      label: 'Net sales',
      data: top.map(r => r.netSales),
      backgroundColor: SERIES[0],
      borderRadius: 4,
      maxBarThickness: 22,
    }],
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card tour="loc-bar" title="Net Sales by Location" sub={`${niceDate(startDate)} – ${niceDate(endDate)} · top ${top.length}`}>
        <div className="chart-box tall">
          <Bar
            data={barData}
            options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: ctx => {
                      const r = top[ctx.dataIndex];
                      return ` ${money(r.netSales)} · ${int(r.orders)} orders · AOV ${money(r.avgOrderValue)}`;
                    },
                  },
                },
              },
              scales: {
                x: { ticks: { callback: v => money(v, { cents: false }) }, beginAtZero: true },
                y: { grid: { display: false }, ticks: { autoSkip: false } },
              },
            }}
          />
        </div>
      </Card>

      <Card
        tour="loc-scorecard"
        title="Location Scorecard"
        sub="sales per labor hour uses recorded shift hours (shifts with valid clock-out only)"
      >
        <DataTable
          csvName="location-scorecard.csv"
          defaultSort={{ key: 'netSales', dir: 'desc' }}
          columns={[
            { key: 'locationName', label: 'Location' },
            { key: 'netSales', label: 'Net Sales', num: true, render: r => money(r.netSales), csv: r => (r.netSales / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.netSales, 0)) },
            {
              key: 'change', label: compareMode === 'priorYear' ? 'vs LY' : 'vs Prior', num: true,
              sortValue: r => (r.prevNetSales ? (r.netSales - r.prevNetSales) / r.prevNetSales : -Infinity),
              render: r => <Delta curr={r.netSales} prev={r.prevNetSales} />,
              csv: r => r.prevNetSales ? (((r.netSales - r.prevNetSales) / r.prevNetSales) * 100).toFixed(1) : '',
            },
            { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders), csv: r => r.orders, footer: rows => int(rows.reduce((a, r) => a + r.orders, 0)) },
            { key: 'avgOrderValue', label: 'AOV', num: true, render: r => money(r.avgOrderValue), csv: r => (r.avgOrderValue / 100).toFixed(2) },
            { key: 'tips', label: 'Tips', num: true, render: r => money(r.tips), csv: r => (r.tips / 100).toFixed(2) },
            {
              key: 'onlineShare', label: 'Online %', num: true,
              sortValue: r => (r.orders ? r.onlineOrders / r.orders : 0),
              render: r => r.orders ? `${((r.onlineOrders / r.orders) * 100).toFixed(0)}%` : '—',
              csv: r => r.orders ? ((r.onlineOrders / r.orders) * 100).toFixed(1) : '',
            },
            {
              key: 'laborHours', label: 'Labor Hrs', num: true,
              render: r => r.laborHours != null ? int(Math.round(r.laborHours)) : '—',
              csv: r => r.laborHours ?? '',
            },
            {
              key: 'salesPerLaborHour', label: '$ / Labor Hr', num: true,
              render: r => r.salesPerLaborHour != null ? money(r.salesPerLaborHour) : '—',
              csv: r => r.salesPerLaborHour != null ? (r.salesPerLaborHour / 100).toFixed(2) : '',
            },
          ]}
          rows={data.map(r => ({ ...r, __key: r.locationId }))}
          footer
        />
        <div className="legend-note">
          Locations with “—” in labor columns have no usable shift punches for the period.
        </div>
      </Card>
    </div>
  );
}
